/* SHARED DATE UTILITIES
   ──────────────────────
   Build the model's month window (allMonths) dynamically from a company's
   historical_start through forecast_end. Replaces the hardcoded
   ALL_M = 2024-01..2026-12 arrays scattered across every engine.
*/

// Build ["YYYY-MM", ...] from historicalStart (inclusive) to forecastEnd (inclusive).
// Both inputs are "YYYY-MM" strings.
export function buildAllMonths(historicalStart, forecastEnd) {
  // Build 1b: a company that has not completed Step 1 has no forecast horizon
  // (companies.forecast_through is NULL once the column became nullable). Return
  // an empty window rather than TypeError on `.split(null)` — the honest-empty
  // path. Callers must tolerate []. Unreachable in the live flow (is_onboarded
  // gates the nav switcher; Step 1 requires forecast_through before Step 8
  // selectability), but the guard makes a forced null fail SAFE, not crash.
  if (!historicalStart || !forecastEnd) return [];
  const [startY, startM] = historicalStart.split("-").map(Number);
  const [endY, endM] = forecastEnd.split("-").map(Number);
  const result = [];
  let y = startY, m = startM;
  while (y < endY || (y === endY && m <= endM)) {
    result.push(`${y}-${String(m).padStart(2, "0")}`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return result;
}

// Return the month string one before m (e.g. "2024-01" -> "2023-12").
// Does NOT require m to be inside allMonths — pure arithmetic on the YYYY-MM string.
export function subtractOneMonth(m) {
  const [y, mo] = m.split("-").map(Number);
  let py = y, pm = mo - 1;
  if (pm < 1) { pm = 12; py -= 1; }
  return `${py}-${String(pm).padStart(2, "0")}`;
}

// Previous month within allMonths. Returns null for the first entry (or unknown m).
export function prevMonth(allMonths, m) {
  const i = allMonths.indexOf(m);
  return i > 0 ? allMonths[i - 1] : null;
}

const MONTH_NAMES_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTH_NAMES_LONG  = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/**
 * Add N months to a "YYYY-MM" string. Handles negative N (subtract).
 * Uses absolute indexing (y*12+m-1+n) plus the positive-modulo idiom
 * ((total % 12) + 12) % 12, defending against the JS signed-modulo bug
 * class that produced malformed strings like "2023--1" before commit
 * 2792b9b. Safe for any signed N and any year ≥ 1.
 *
 * Replaces the local mIdx/mFromIdx/mAdd helpers in runFullModel and
 * the local addMonths copies in computeAEQuotaDR / computeCompanyQuotaDR /
 * computeImplementationRevenue. Will be migrated incrementally during
 * the page sweep workstream.
 *
 * @param {string} monthStr - "YYYY-MM"
 * @param {number} n - integer offset, can be negative
 * @returns {string} resulting "YYYY-MM"
 *
 * @example addMonths("2024-01",  1)  // "2024-02"
 * @example addMonths("2024-01", -1)  // "2023-12"
 * @example addMonths("2024-01", -13) // "2022-12"
 * @example addMonths("2023-04", -9)  // "2022-07" — the AE-Model-vendor case
 */
export function addMonths(monthStr, n) {
  const [y, m] = monthStr.split("-").map(Number);
  const total = (y * 12 + (m - 1)) + n;
  const ny = Math.floor(total / 12);
  const nm = ((total % 12) + 12) % 12 + 1;
  return `${ny}-${String(nm).padStart(2, "0")}`;
}

/**
 * Months from `a` to `b` as a signed integer. Inverse of addMonths:
 * addMonths(a, monthOffset(a, b)) === b for any valid (a, b).
 * Positive when b > a, negative when b < a, zero when equal.
 *
 * @param {string} a - "YYYY-MM"
 * @param {string} b - "YYYY-MM"
 * @returns {number} months from a to b (b - a)
 *
 * @example monthOffset("2024-01", "2024-04") //  3
 * @example monthOffset("2024-04", "2024-01") // -3
 * @example monthOffset("2023-12", "2024-01") //  1
 */
export function monthOffset(a, b) {
  const [ay, am] = a.split("-").map(Number);
  const [by, bm] = b.split("-").map(Number);
  return (by * 12 + (bm - 1)) - (ay * 12 + (am - 1));
}

/**
 * Next month within allMonths. Returns null for the last entry or unknown m.
 * Companion to existing prevMonth.
 *
 * @param {string[]} allMonths
 * @param {string} m
 * @returns {string|null}
 */
export function nextMonth(allMonths, m) {
  const i = allMonths.indexOf(m);
  return i >= 0 && i < allMonths.length - 1 ? allMonths[i + 1] : null;
}

/**
 * Filter to actuals (months ≤ cutoff). Cutoff is INCLUSIVE — the cutoff
 * month itself is the last actuals month.
 *
 * @param {string[]} allMonths
 * @param {string} cutoff
 * @returns {string[]}
 */
export function filterActuals(allMonths, cutoff) {
  return allMonths.filter(m => m <= cutoff);
}

/**
 * Filter to forecast (months > cutoff). Cutoff is EXCLUSIVE — the first
 * forecast month is the one immediately after cutoff.
 *
 * @param {string[]} allMonths
 * @param {string} cutoff
 * @returns {string[]}
 */
export function filterForecast(allMonths, cutoff) {
  return allMonths.filter(m => m > cutoff);
}

/**
 * @param {string} monthStr "YYYY-MM"
 * @returns {number} 4-digit year as integer
 */
export function getYear(monthStr) {
  return Number(monthStr.split("-")[0]);
}

/**
 * @param {string} monthStr "YYYY-MM"
 * @returns {number} 1-12
 */
export function getMonthNumber(monthStr) {
  return Number(monthStr.split("-")[1]);
}

/**
 * @param {string} monthStr "YYYY-MM"
 * @returns {number} 1-4
 */
export function getQuarter(monthStr) {
  return Math.ceil(Number(monthStr.split("-")[1]) / 3);
}

/**
 * Group months by calendar year. Year keys are integers (sortable).
 * Iterate via Object.keys(...).map(Number).sort((a,b) => a-b).
 *
 * @param {string[]} months
 * @returns {Object<number, string[]>}
 *
 * @example groupByYear(["2024-01","2024-02","2025-01"])
 *   → { 2024: ["2024-01","2024-02"], 2025: ["2025-01"] }
 */
export function groupByYear(months) {
  const out = {};
  for (const m of months) {
    const y = getYear(m);
    (out[y] = out[y] || []).push(m);
  }
  return out;
}

/**
 * Group months by calendar quarter. Keys are "YYYY-QN" (lexicographically
 * sortable: Q1 < Q2 < Q3 < Q4).
 *
 * @param {string[]} months
 * @returns {Object<string, string[]>}
 *
 * @example groupByQuarter(["2024-01","2024-02","2024-04"])
 *   → { "2024-Q1": ["2024-01","2024-02"], "2024-Q2": ["2024-04"] }
 */
export function groupByQuarter(months) {
  const out = {};
  for (const m of months) {
    const key = `${getYear(m)}-Q${getQuarter(m)}`;
    (out[key] = out[key] || []).push(m);
  }
  return out;
}

/**
 * Format a month string for display.
 *
 * @param {string} m "YYYY-MM"
 * @param {"short"|"shortYear"|"long"|"numeric"|"fy"} [fmt="short"]
 *   - "short":     "Jan"            (month name only)
 *   - "shortYear": "Jan '24"        (month + 2-digit year, apostrophe)
 *   - "long":      "January 2024"   (full month name + 4-digit year)
 *   - "numeric":   "2024-01"        (passthrough)
 *   - "shortFull":  "Jan 2024"       (SHORT month name + 4-digit year)
 *   - "fy":        "FY24"           (fiscal-year shorthand for year-grouped contexts)
 *
 * ★ A MISSING OR BLANK MONTH RETURNS THE EM-DASH "—", never a throw. This is a
 *   DISPLAY formatter with ZERO engine importers (measured: 30 engine files
 *   scanned, none references it), so §12.5 rule 14's "guard an ENGINE input at the
 *   call site" does not reach it — the rule's other half applies: guard a DISPLAY
 *   helper ONCE, at the function, and it is correct at every call site including
 *   the N+1th. Before consolidation the 19 page-local copies answered a blank
 *   month SIX different ways (undefined / "—" / a thrown TypeError / "undefined '"
 *   / "undefined " / ""); this is the one answer, and the five copies that already
 *   returned "—" are the ones it matches.
 * @returns {string}
 */
export function formatMonthLabel(m, fmt = "short") {
  // ★ THE LEADING GUARD — before getYear/getMonthNumber, which are what throw on a
  // null/blank month (they .split() it unguarded, by design: they are ENGINE inputs).
  if (!m) return "\u2014";
  const y = getYear(m);
  const mo = getMonthNumber(m);
  const yStr = String(y);
  switch (fmt) {
    case "short":     return MONTH_NAMES_SHORT[mo - 1];
    case "shortYear": return `${MONTH_NAMES_SHORT[mo - 1]} '${yStr.slice(-2)}`;
    case "long":      return `${MONTH_NAMES_LONG[mo - 1]} ${yStr}`;
    // ★ SHORT month + FOUR-digit year. "long" is NOT this: it emits the FULL month
    // name ("March 2023"), a different and visibly wider string in a 36-option
    // <select>. Five page-local copies emitted this shape and none could adopt
    // formatMonthLabel without it.
    case "shortFull": return `${MONTH_NAMES_SHORT[mo - 1]} ${yStr}`;
    case "numeric":   return m;
    case "fy":        return `FY${yStr.slice(-2)}`;
    default:          return m;
  }
}
